// Shared helpers for matching customers/contacts by name OR phone, regardless
// of the phone format the user types.
//
// GHL stores phone numbers in E.164 (e.g. "+923004075038"). Users usually type
// the local form ("03004075038") or just part of it ("3004075038", "4075038").
// A plain substring match on "03004075038" never hits "+923004075038" because
// the stored value has no leading 0 and carries the "92" country code. These
// helpers strip both sides down to the significant digits before comparing.

/** Reduce a phone-ish string to its significant national digits. */
export const phoneCore = (value = "") => {
  let d = String(value).replace(/\D/g, "");
  // drop country code (92 for PK) when the number is clearly international
  if (d.length > 10 && d.startsWith("92")) d = d.slice(2);
  // drop trunk/leading zero(s)
  d = d.replace(/^0+/, "");
  return d;
};

/**
 * Does this customer/contact match the typed search term?
 * Checks name, email and phone. Phone is matched in any format
 * ("0300...", "300...", "+92300...", "92300...", trailing fragment).
 *
 * `row` accepts either the table shape { name, email, phone } or the raw
 * GHL contact { contactName, firstName, lastName, email, phone }.
 */
export const matchesCustomer = (row, rawInput) => {
  const input = (rawInput || "").toLowerCase().trim();
  if (!input) return true;
  if (!row) return false;

  const name = (
    row.name ||
    row.contactName ||
    `${row.firstName || ""} ${row.lastName || ""}`
  )
    .toString()
    .toLowerCase();
  const email = (row.email || "").toString().toLowerCase();

  if (name.includes(input) || email.includes(input)) return true;

  const inputDigits = input.replace(/\D/g, "");
  if (inputDigits.length >= 3) {
    const phoneDigits = String(row.phone || "").replace(/\D/g, "");
    if (phoneDigits && phoneDigits.includes(inputDigits)) return true;

    const a = phoneCore(phoneDigits);
    const b = phoneCore(inputDigits);
    if (a && b && (a.includes(b) || b.includes(a))) return true;
  }
  return false;
};

/**
 * react-select `filterOption` for the "Existing Customer" dropdown.
 * Delegates to matchesCustomer using the option's data payload.
 */
export const customerFilterOption = (option, rawInput) => {
  if (!rawInput || !rawInput.trim()) return true;
  const row = option?.data || {};
  return matchesCustomer(
    { name: option?.label, email: row.email, phone: row.phone },
    rawInput
  );
};
